import { type PaletteColor, type Theme } from '@mui/material/styles';

import { type ExtractValues } from '@craft/lib';

export class InvalidColorError extends Error {
  constructor(value: string) {
    super(`The color, '${JSON.stringify(value)}', is invalid!`);
  }
}

const MuiPaletteLessColors = ['white', 'transparent'] as const;
type MuiPalettelessColor = (typeof MuiPaletteLessColors)[number];

export const isMuiPalettelessColor = (color: unknown): color is MuiPalettelessColor =>
  MuiPaletteLessColors.includes(color as MuiPalettelessColor);

/**
 * Represents the primary color keys that exist in the Material UI {@link Theme} object, under
 * the 'palette' property.
 *
 * At the time of this writing, this contains the following:
 * "primary" | "secondary" | "error" | "warning" | "info" | "success" | "pale" | "pending"
 *   | "alert" | "other"
 */
export type MuiPalettePrimaryColorKey =
  | 'action'
  | 'alert'
  | 'background'
  | 'other'
  | 'text'
  | keyof {
      [key in keyof Theme['palette'] as Theme['palette'][key] extends PaletteColor
        ? key
        : never]: key;
    };

/**
 * An object that is used to ensure that the typeguard {@link isMuiPalettePrimaryColorKey} is in
 * sync with the definition of {@link MuiPalettePrimaryColorKey} which is derived from the
 * Material UI {@link Theme} object.
 *
 * If new color keys are ever added to or removed from the {@link Theme} object, TypeScript
 * compilation will fail for this definition until it is updated with those new or removed keys.
 */
const MuiPalettePrimaryColorKeys: Record<MuiPalettePrimaryColorKey, true> = {
  action: true,
  alert: true,
  background: true,
  chip: true,
  error: true,
  info: true,
  other: true,
  pale: true,
  pending: true,
  primary: true,
  secondary: true,
  success: true,
  text: true,
  warning: true,
};

/**
 * A typeguard that returns whether or not the provided value, {@link unknown}, is a valid
 * Material UI primary color key, {@link MuiPalettePrimaryColorKey}, based on the definition of
 * {@link MuiPalettePrimaryColorKeys}.
 *
 * @param {unknown} color
 *   The value for which the determination should be made as to whether or not it is of type
 *   {@link MuiPalettePrimaryColorKey}.
 *
 * @returns {color is MuiPalettePrimaryColorKey}
 *   Whether or not the provided value is a valid Material UI primary color key,
 *   {@link MuiPalettePrimaryColorKey}.
 */
export const isMuiPalettePrimaryColorKey = (color: unknown): color is MuiPalettePrimaryColorKey =>
  typeof color === 'string' && Object.keys(MuiPalettePrimaryColorKeys).includes(color);

export const DefaultSecondaryColorNames = {
  action: 'active',
  background: 'paper',
  chip: 'main',
  error: 'main',
  info: 'main',
  pale: 'main',
  pending: 'main',
  primary: 'main',
  secondary: 'main',
  success: 'main',
  text: 'primary',
  warning: 'main',
} as const satisfies {
  [key in Exclude<MuiPalettePrimaryColorKey, 'alert' | 'other'>]: keyof Theme['palette'][key];
};

/**
 * Represents a subset of {@link MuiPalettePrimaryColorKey} for which the key is associated with
 * a reasonable default, secondary color name, and can be used as a standalone color.
 *
 * For instance, this type includes "primary", because when referenced alone, "primary" can be
 * treated as "primary.main", based on the definition of {@link DefaultSecondaryColorNames}.
 * However, the type does not include "other", because "other" cannot be associated with a
 * reasonable default.
 *
 * This means that "primary" will be a valid color specification, but "other" will not be.  Instead,
 * "other" must be used with a secondary color name, such as "other.avatarFill".
 */
export type MuiPalettePrimaryColorName = Extract<
  MuiPalettePrimaryColorKey,
  keyof typeof DefaultSecondaryColorNames
>;

/**
 * An object that is used to ensure that the typeguard {@link isMuiPalettePrimaryColorName} is in
 * sync with the definition of {@link MuiPalettePrimaryColorName} which is derived from the
 * Material UI {@link Theme} object.
 *
 * If new primary color names are ever added to the {@link Theme} object, TypeScript compilation
 * will fail on this map until it is updated.
 */
const MuiPalettePrimaryColorNamesMap: Record<MuiPalettePrimaryColorName, true> = {
  action: true,
  background: true,
  chip: true,
  error: true,
  info: true,
  pale: true,
  pending: true,
  primary: true,
  secondary: true,
  success: true,
  text: true,
  warning: true,
};

/**
 * A typeguard that returns whether or not the provided value, {@link unknown}, is a valid
 * Material UI primary color name, {@link MuiPalettePrimaryColorName}.
 *
 * @param {unknown} color
 *   The value for which the determination should be made as to whether or not it is of type
 *   {@link MaterialUIPrimaryColorName}.
 *
 * @returns {color is MuiPalettePrimaryColorName}
 *   Whether or not the provided value is a valid Material UI primary color name,
 *   {@link MuiPalettePrimaryColorName}.
 */
const isMuiPalettePrimaryColorName = (color: unknown): color is MuiPalettePrimaryColorName =>
  typeof color === 'string' && Object.keys(MuiPalettePrimaryColorNamesMap).includes(color);

type MuiPaletteSecondaryColorName<N extends MuiPalettePrimaryColorKey = MuiPalettePrimaryColorKey> =
  N extends MuiPalettePrimaryColorKey
    ? keyof {
        [key in keyof Theme['palette'][N] as Theme['palette'][N][key] extends string
          ? key
          : never]: never;
      }
    : never;

export type MuiPaletteFullColorName = ExtractValues<{
  [k1 in MuiPalettePrimaryColorKey]: keyof {
    [k2 in MuiPaletteSecondaryColorName<k1> as `${k1}.${k2}`]: k2;
  };
}>;

/**
 * Represents the full set of valid Material UI color names, {@link MuiPaletteColorName}, which are
 * derived from the Material UI {@link Theme} object itself.
 *
 * @example
 * 'primary' | 'primary.main' | 'secondary.main' | 'secondary.dark' | ...
 */
export type MuiPaletteColorName =
  | MuiPaletteFullColorName
  | MuiPalettelessColor
  | MuiPalettePrimaryColorName;

const isMuiPaletteSecondaryColorName = <N extends MuiPalettePrimaryColorKey>(
  theme: Theme,
  color: unknown,
  primary: N,
): color is MuiPaletteSecondaryColorName<N> => {
  const palette = theme.palette[primary];
  return (
    typeof palette === 'object' &&
    typeof color === 'string' &&
    color in palette &&
    typeof palette[color as keyof typeof palette] === 'string'
  );
};

type ParsedValues<P extends MuiPalettePrimaryColorKey = MuiPalettePrimaryColorKey> =
  P extends MuiPalettePrimaryColorKey
    ? [Extract<P, MuiPalettePrimaryColorName>, null] | [P, MuiPaletteSecondaryColorName<P>]
    : never;

type ParsedColorName<
  T extends string,
  O extends EvaluatorColorOptions,
> = T extends `${infer P extends MuiPalettePrimaryColorName}.${infer S extends string}`
  ? S extends MuiPaletteSecondaryColorName<P>
    ? [P, S]
    : never
  : T extends MuiPalettePrimaryColorName
    ? [T, null]
    : T extends string
      ? O extends { strict: false }
        ? null | ParsedValues
        : ParsedValues
      : never;

export interface EvaluatorColorOptions {
  readonly strict?: boolean;
}

export const parseMuiPaletteColorName = <T extends string, O extends EvaluatorColorOptions>(
  theme: Theme,
  color: T,
  options?: O,
): ParsedColorName<T, O> => {
  if (typeof color === 'string') {
    if (isMuiPalettePrimaryColorName(color)) {
      return [color, null] as ParsedColorName<T, O>;
    }
    if ((color.match(/\./g) ?? []).length === 1) {
      const primary = color.split('.')[0];
      const secondary = color.split('.')[1];
      if (
        isMuiPalettePrimaryColorKey(primary) &&
        isMuiPaletteSecondaryColorName(theme, secondary, primary)
      ) {
        return [primary, secondary] as ParsedColorName<T, O>;
      }
    }
  }
  if (options?.strict === false) {
    return null as ParsedColorName<T, O>;
  }
  throw new InvalidColorError(color);
};
